import React, { useState, useMemo, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Save, Trash2, Plus, Search, DollarSign, Users, Calendar, Download,
  Eye, Edit2, X, Check, ChevronRight, FileText, Upload, Package,
  CreditCard, Percent, Clock, CheckCircle, ArrowRight, ArrowLeft, Loader2,
  Settings, Zap, TrendingUp, Calculator, Home, FileDown, FolderPlus, AlertCircle
} from 'lucide-react';
import quickBudgetService from '../services/quickBudgetService';
import { clientService } from '../services/api';
import inventoryService from '../services/inventoryService';
import cardRatesService from '../services/cardRatesService';
import toast from 'react-hot-toast';
import PdfPreviewModal from './budget/PdfPreviewModal';
import { mapQuickBudgetToTemplate } from './budget/mapQuickBudgetToTemplate';

const STEPS = [
  { id: 1, name: 'Cliente', icon: Users },
  { id: 2, name: 'Projeto', icon: FileText },
  { id: 3, name: 'Ambientes', icon: Home },
  { id: 4, name: 'Consumíveis', icon: Package },
  { id: 5, name: 'Precificação', icon: Calculator },
  { id: 6, name: 'Revisão', icon: Eye },
  { id: 7, name: 'Resumo', icon: CheckCircle }
];

const DEFAULT_DIFERENCIAIS = [
  'MDF Ultra',
  'Dobradiças Blum',
  'Corrediças Invisíveis',
  'Garantia',
  'Projeto 3D'
];

const DEFAULT_INCLUSOES = [
  'Projeto Executivo',
  'Medição',
  'Fabricação',
  'Transporte',
  'Montagem',
  'Garantia'
];

const DEFAULT_NAO_INCLUSOES = [
  'Elétrica',
  'Gesso',
  'Granito',
  'Pintura',
  'Hidráulica'
];

const QuickBudget = () => {
  const navigate = useNavigate();
  const [createdBudgetModal, setCreatedBudgetModal] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [cortesPdfId, setCortesPdfId] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  const [items, setItems] = useState([]);
  const [projectName, setProjectName] = useState('');

  // Ambientes - nova estrutura
  const [ambientes, setAmbientes] = useState([]);
  const [editingAmbienteId, setEditingAmbienteId] = useState(null);
  const [showAmbienteModal, setShowAmbienteModal] = useState(false);
  const [editingAmbiente, setEditingAmbiente] = useState({ nome: '', descricao: '' });

  // Estado temporário para edição de ambiente (quando editingAmbienteId está setado)
  const [tempAmbienteItems, setTempAmbienteItems] = useState([]);
  const [tempMatchingResults, setTempMatchingResults] = useState([]);
  const [tempPieceSelections, setTempPieceSelections] = useState({});
  const [tempManualPrices, setTempManualPrices] = useState({});
  const [tempPdfData, setTempPdfData] = useState(null);
  const [tempCortesPdfId, setTempCortesPdfId] = useState(null);

  const [pricingType, setPricingType] = useState('TOTAL');
  const [pricePerSqm, setPricePerSqm] = useState(0);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [validityDays, setValidityDays] = useState(30);
  const [notes, setNotes] = useState('');

  const [hasDownPayment, setHasDownPayment] = useState(false);
  const [downPaymentPercent, setDownPaymentPercent] = useState(30);
  const [downPaymentMethod, setDownPaymentMethod] = useState('DINHEIRO');
  const [downPaymentHasDiscount, setDownPaymentHasDiscount] = useState(false);
  const [downPaymentDiscountPercent, setDownPaymentDiscountPercent] = useState(5);
  const [installmentCount, setInstallmentCount] = useState(12);
  const [installmentMethod, setInstallmentMethod] = useState('CREDIT');
  const [hasInterest, setHasInterest] = useState(false);
  const [cardRates, setCardRates] = useState([]);
  const [ratePaidBy, setRatePaidBy] = useState('empresa');

  const [diferenciais, setDiferenciais] = useState(DEFAULT_DIFERENCIAIS);
  const [inclusoes, setInclusoes] = useState(DEFAULT_INCLUSOES);
  const [naoInclusoes, setNaoInclusoes] = useState(DEFAULT_NAO_INCLUSOES);

  const [consultantName, setConsultantName] = useState('');
  const [consultantPhone, setConsultantPhone] = useState('');

  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  const [matchingResults, setMatchingResults] = useState([]);
  const [ambienteMatchResults, setAmbienteMatchResults] = useState({});
  const [isMatching, setIsMatching] = useState(false);
  const [pieceSelections, setPieceSelections] = useState({});
  const [manualPrices, setManualPrices] = useState({});
  const [selectedVariant, setSelectedVariant] = useState({});

  const [hasNoCuttingPlan, setHasNoCuttingPlan] = useState(false);
  const [manualBudgetMode, setManualBudgetMode] = useState('TOTAL');
  const [manualTotalValue, setManualTotalValue] = useState('');
  const [manualItems, setManualItems] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [isSearchingProducts, setIsSearchingProducts] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  const [consumables, setConsumables] = useState([]);
  const [consumableSearch, setConsumableSearch] = useState('');
  const [consumableSearchResults, setConsumableSearchResults] = useState([]);
  const [isSearchingConsumables, setIsSearchingConsumables] = useState(false);
  const [showConsumableDropdown, setShowConsumableDropdown] = useState(false);

  const [pricingConfig, setPricingConfig] = useState(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);

  const [complexityMultiplier, setComplexityMultiplier] = useState(1.0);
  const [complexityLevel, setComplexityLevel] = useState(2);
  const [preferences, setPreferences] = useState({
    material_type: { key: '1', weight: 0, label: 'Tipo 1' },
    door_finish: { key: 'Liso', weight: 0, label: 'Liso' },
    hardware_type: { key: '1', weight: 0, label: 'Tipo 1' },
    has_glass: { key: 'false', weight: 0, label: 'Sem Vidro' }
  });
  const [marginPercent, setMarginPercent] = useState(100);

  const [calculatedPricing, setCalculatedPricing] = useState(null);

  useEffect(() => {
    fetchClients();
    fetchPricingConfig();
    fetchCardRates();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.product-search-dropdown')) {
        setShowProductDropdown(false);
      }
      if (!e.target.closest('.consumable-search-dropdown')) {
        setShowConsumableDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchClients = async (search = '') => {
    try {
      setLoadingClients(true);
      const response = await clientService.getClients({ search, limit: 50 });
      setClients(response.data.data || []);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
    } finally {
      setLoadingClients(false);
    }
  };

  const fetchPricingConfig = async () => {
    try {
      setIsLoadingConfig(true);
      const response = await quickBudgetService.getPricingConfig();
      if (response.success) {
        setPricingConfig(response.data);
        if (response.data.complexity?.length > 0) {
          const defaultComplexity = response.data.complexity.find(c => c.value === 2) || response.data.complexity[0];
          if (defaultComplexity) {
            setComplexityLevel(defaultComplexity.value);
            setComplexityMultiplier(parseFloat(defaultComplexity.multiplier));
          }
        }
        if (response.data.materials?.length > 0) {
          const mats = response.data.materials;
          const getDefault = (type, fallbackKey) => {
            const found = mats.find(m => m.type === type);
            return found ? { key: found.value, weight: parseFloat(found.weight), label: found.label } : null;
          };
          setPreferences({
            material_type: getDefault('material_type', '1') || { key: '1', weight: 0, label: 'Tipo 1' },
            door_finish: getDefault('door_finish', 'Liso') || { key: 'Liso', weight: 0, label: 'Liso' },
            hardware_type: getDefault('hardware_type', '1') || { key: '1', weight: 0, label: 'Tipo 1' },
            has_glass: getDefault('has_glass', 'false') || { key: 'false', weight: 0, label: 'Sem Vidro' }
          });
        }
      }
    } catch (error) {
      console.error('Erro ao buscar configuração de precificação:', error);
    } finally {
      setIsLoadingConfig(false);
    }
  };

  const fetchCardRates = async () => {
    try {
      const response = await cardRatesService.getAllRates();
      if (response.success && response.data) {
        setCardRates(response.data);
      }
    } catch (error) {
      console.error('Erro ao buscar taxas de cartão:', error);
    }
  };

  const getCardRate = (cardBrand, installments) => {
    if (!cardRates || cardRates.length === 0) return 0;
    const rate = cardRates.find(r => r.card_brand?.toUpperCase() === cardBrand?.toUpperCase() && r.installments === installments);
    return rate ? rate.rate_percentage / 100 : 0;
  };

  const searchProducts = async (query) => {
    if (!query || query.length < 2) {
      setProductSearchResults([]);
      setShowProductDropdown(false);
      return;
    }
    try {
      setIsSearchingProducts(true);
      const response = await inventoryService.getVariantsForBudget({ search: query });
      if (response.success) {
        setProductSearchResults(response.data || []);
        setShowProductDropdown(true);
      }
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
    } finally {
      setIsSearchingProducts(false);
    }
  };

  const handleSelectProduct = (product) => {
    const width = parseFloat(product.comprimento_cm) || 0;
    const height = parseFloat(product.largura_cm) || 0;
    const area = width && height ? (width * height) / 10000 : 0;
    const totalPrice = parseFloat(product.preco_venda) || 0;
    const pricePerSqm = area > 0 ? totalPrice / area : totalPrice;

    setManualItems([...manualItems, {
      variant_id: product.variant_id,
      name: product.produto_nome,
      quantity: 1,
      width: width,
      height: height,
      price: totalPrice,
      pricePerSqm: pricePerSqm,
      material: product.categoria_nome || '',
      color: product.cor || '',
      finish: product.acabamento || '',
      thickness: product.espessura_mm || '',
      area: area,
      measure_text: product.medida_text || '',
      estoque: product.estoque_disponivel || 0
    }]);
    setProductSearch('');
    setProductSearchResults([]);
    setShowProductDropdown(false);
  };

  const recalculateItemPrice = (item) => {
    const width = parseFloat(item.width) || 0;
    const height = parseFloat(item.height) || 0;
    const area = width && height ? (width * height) / 10000 : 0;
    const qty = parseInt(item.quantity) || 1;
    return qty * area * (item.pricePerSqm || 0);
  };

  const searchConsumables = async (query) => {
    if (!query || query.length < 2) {
      setConsumableSearchResults([]);
      setShowConsumableDropdown(false);
      return;
    }
    try {
      setIsSearchingConsumables(true);
      const response = await inventoryService.getVariantsForBudget({ search: query });
      if (response.success) {
        setConsumableSearchResults(response.data || []);
        setShowConsumableDropdown(true);
      }
    } catch (error) {
      console.error('Erro ao buscar consumíveis:', error);
    } finally {
      setIsSearchingConsumables(false);
    }
  };

  const handleSelectConsumable = (product) => {
    setConsumables([...consumables, {
      variant_id: product.variant_id,
      name: product.produto_nome,
      sku: product.sku_variacao || product.produto_sku,
      quantity: 1,
      unit_price: parseFloat(product.preco_venda) || 0,
      material: product.categoria_nome || '',
      color: product.cor || '',
      finish: product.acabamento || '',
      espessura: product.espessura_mm || '',
      estoque: product.estoque_disponivel || 0,
      measure_text: product.medida_text || ''
    }]);
    setConsumableSearch('');
    setConsumableSearchResults([]);
    setShowConsumableDropdown(false);
  };

  const totalConsumables = useMemo(() => {
    return consumables.reduce((sum, c) => sum + (c.quantity * c.unit_price), 0);
  }, [consumables]);

  const filteredClients = clients.filter(c =>
    (c.full_name || c.name || '').toLowerCase().includes(clientSearch.toLowerCase()) ||
    (c.phone || '').includes(clientSearch) ||
    (c.email || '').toLowerCase().includes(clientSearch.toLowerCase())
  );

  const getItemTotal = (item) => {
    if (item.source === 'pdf') {
      return (parseFloat(item.quantity) || 1) * (parseFloat(item.unit_price) || 0);
    }
    return parseFloat(item.unit_price) || 0;
  };

  const getAmbientesItemsTotal = () => {
    return ambientes.reduce((ambTotal, amb) => {
      const ambItemsTotal = (amb.items || []).reduce((sum, item) => sum + getItemTotal(item), 0);
      return ambTotal + ambItemsTotal;
    }, 0);
  };

  const getAmbientesConsumablesTotal = () => {
    return ambientes.reduce((ambTotal, amb) => {
      const ambConsTotal = (amb.consumables || []).reduce((sum, c) => sum + ((parseFloat(c.quantity) || 1) * (parseFloat(c.unit_price) || 0)), 0);
      return ambTotal + ambConsTotal;
    }, 0);
  };

  const getAllItemsTotal = () => {
    return getAmbientesItemsTotal();
  };

  const getAllConsumablesTotal = () => {
    return totalConsumables + getAmbientesConsumablesTotal();
  };

  const baseSubtotal = useMemo(() => {
    if (pricingType === 'SQM' && pricePerSqm > 0) {
      let totalArea = items.reduce((sum, item) => {
        if (item.width_cm && item.height_cm) {
          return sum + ((parseFloat(item.width_cm) * parseFloat(item.height_cm)) / 10000);
        }
        return sum;
      }, 0);
      ambientes.forEach(amb => {
        (amb.items || []).forEach(item => {
          if (item.largura_cm && item.comprimento_cm) {
            totalArea += (parseFloat(item.largura_cm) * parseFloat(item.comprimento_cm)) / 10000;
          }
        });
      });
      return totalArea * pricePerSqm;
    }

    const itemsTotal = items.reduce((sum, item) => {
      return sum + getItemTotal(item);
    }, 0);
    return itemsTotal + getAmbientesItemsTotal();
  }, [items, ambientes, pricingType, pricePerSqm]);

  const totalPreferenceWeight = useMemo(() => {
    return Object.values(preferences).reduce((sum, p) => sum + (p.weight || 0), 0);
  }, [preferences]);

  const pricingCalculation = useMemo(() => {
    const basePrice = baseSubtotal + totalConsumables + getAmbientesConsumablesTotal();
    const complexityAdjusted = basePrice * complexityMultiplier;
    const materialAdjusted = complexityAdjusted * (1 + totalPreferenceWeight / 100);
    const withMargin = materialAdjusted * (1 + marginPercent / 100);

    let finalPrice = withMargin;
    let interestAmount = 0;
    let paymentLabel = '';
    let discountAmount = 0;

    if (downPaymentHasDiscount && downPaymentDiscountPercent > 0) {
      discountAmount = withMargin * (downPaymentDiscountPercent / 100);
      finalPrice = withMargin - discountAmount;
    }

    const downPaymentAmount = hasDownPayment ? finalPrice * (downPaymentPercent / 100) : 0;
    const remaining = finalPrice - downPaymentAmount;

    if (installmentCount > 1) {
      const cardRate = getCardRate('CREDIT', installmentCount);
      const shouldApplyRate = installmentMethod === 'CREDIT' && ratePaidBy === 'cliente' && cardRate > 0;

      if (shouldApplyRate) {
        interestAmount = remaining * cardRate;
        finalPrice = finalPrice + interestAmount;
        paymentLabel = hasDownPayment
          ? `Entrada ${downPaymentPercent}% + ${installmentCount}x (Taxa de ${(cardRate * 100).toFixed(2)}% incluída)`
          : `${installmentCount}x (Taxa de ${(cardRate * 100).toFixed(2)}% incluída)`;
      } else {
        paymentLabel = hasDownPayment
          ? `Entrada ${downPaymentPercent}% + ${installmentCount}x sem juros`
          : `${installmentCount}x sem juros`;
      }
    } else {
      paymentLabel = hasDownPayment
        ? `Entrada ${downPaymentPercent}% + restante`
        : discountAmount > 0
          ? `${downPaymentDiscountPercent}% desconto dinheiro`
          : 'À vista';
    }

    return {
      basePrice: Math.round(basePrice * 100) / 100,
      complexityMultiplier,
      complexityAdjusted: Math.round(complexityAdjusted * 100) / 100,
      totalPreferenceWeight,
      materialAdjusted: Math.round(materialAdjusted * 100) / 100,
      marginPercent,
      withMargin: Math.round(withMargin * 100) / 100,
      paymentLabel,
      finalPrice: Math.round(finalPrice * 100) / 100,
      discountAmount: Math.round(discountAmount * 100) / 100,
      downPaymentAmount: Math.round(downPaymentAmount * 100) / 100,
      remaining: Math.round(remaining * 100) / 100,
      interestAmount: Math.round(interestAmount * 100) / 100,
      parcelValue: installmentCount > 1 ? Math.round((remaining + interestAmount) / installmentCount * 100) / 100 : finalPrice,
      installmentCount,
      hasInterest,
      hasDownPayment,
      downPaymentPercent,
      downPaymentHasDiscount,
      downPaymentDiscountPercent,
      ratePaidBy,
      appliedCardRate: getCardRate('CREDIT', installmentCount)
    };
  }, [baseSubtotal, totalConsumables, ambientes, complexityMultiplier, totalPreferenceWeight, marginPercent, hasDownPayment, downPaymentPercent, installmentCount, hasInterest, downPaymentHasDiscount, downPaymentDiscountPercent, ratePaidBy, cardRates, installmentMethod]);

  useEffect(() => {
    if (items.length > 0) {
      setCalculatedPricing(pricingCalculation);
    }
  }, [pricingCalculation]);

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setClientSearch(client.full_name || client.name || '');
    setCurrentStep(2);
  };

  const openAmbienteEditor = (ambienteId) => {
    const ambiente = ambientes.find(a => a.id === ambienteId);
    if (ambiente) {
      setEditingAmbiente({ nome: ambiente.nome || '', descricao: ambiente.descricao || '' });
    }
    setEditingAmbienteId(ambienteId);
  };

  const handlePdfUpload = async (event, ambienteId, ambienteNome) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Apenas arquivos PDF são permitidos');
      return;
    }

    setIsUploading(true);

    const formData = new FormData();
    formData.append('pdf', file);

    try {
      const response = await quickBudgetService.processPdf(formData);

      if (response.success) {
        if (ambienteId) {
          const extractedItems = response.data.cortes.map((corte, index) => ({
            source: 'pdf',
            corte_detalhe_id: corte.id,
            environment_name: ambienteNome || 'Geral',
            item_name: corte.descricao || `Peça ${index + 1}`,
            description: `${corte.quantidade || 1}x - ${corte.largura_cm || 0}cm x ${corte.comprimento_cm || 0}cm`,
            quantity: corte.quantidade || 1,
            largura_cm: corte.largura_cm,
            comprimento_cm: corte.comprimento_cm,
            espessura_mm: corte.espessura_mm || 18,
            unit_price: 0,
            material: corte.material,
            color: corte.cor,
            matched_variant_id: null,
            selection_type: 'pending'
          }));

          let updatedAmbientes = ambientes.map(a => {
            if (a.id === ambienteId) {
              return { ...a, items: [...(a.items || []), ...extractedItems] };
            }
            return a;
          });

          setAmbientes(updatedAmbientes);
          toast.success(`${response.data.total_cortes} peças extraídas para o ambiente!`);

          setIsMatching(true);
          try {
            const matchResponse = await quickBudgetService.matchPiecesWithStock(extractedItems);
            if (matchResponse.success) {
              const matchResults = matchResponse.data.results;
              const found = matchResponse.data.summary.found;
              const notFound = matchResponse.data.summary.notFound;

              updatedAmbientes = updatedAmbientes.map(a => {
                if (a.id === ambienteId) {
                  const updatedItems = a.items.map((item, idx) => {
                    const matchResult = matchResults[idx];
                    if (matchResult && matchResult.found && matchResult.bestMatch) {
                      return {
                        ...item,
                        matched_variant_id: matchResult.bestMatch.variant_id,
                        selection_type: 'matched',
                        unit_price: matchResult.suggestedPrice || matchResult.bestMatch.preco_venda || 0
                      };
                    }
                    return item;
                  });
                  return { ...a, items: updatedItems };
                }
                return a;
              });
              setAmbientes(updatedAmbientes);

              const newAmbienteMatchResults = { ...ambienteMatchResults };
              newAmbienteMatchResults[ambienteId] = matchResults;
              setAmbienteMatchResults(newAmbienteMatchResults);

              toast.success(`${found} peças encontradas no estoque, ${notFound} não encontradas`);
            }
          } catch (matchError) {
            console.error('Erro ao buscar no estoque:', matchError);
            toast.error('Peças extraídas, mas falha ao buscar no estoque');
          } finally {
            setIsMatching(false);
          }
        } else {
          setCortesPdfId(response.data.id);
          setPdfData(response.data);

          const extractedItems = response.data.cortes.map((corte, index) => ({
            source: 'pdf',
            corte_detalhe_id: corte.id,
            environment_name: 'Geral',
            item_name: corte.descricao || `Peça ${index + 1}`,
            description: `${corte.quantidade || 1}x - ${corte.largura_cm || 0}cm x ${corte.comprimento_cm || 0}cm`,
            quantity: corte.quantidade || 1,
            unit: 'un',
            width_cm: corte.largura_cm,
            height_cm: corte.comprimento_cm,
            depth_cm: 18,
            unit_price: 0,
            total_price: 0,
            material: corte.material,
            color: corte.cor
          }));

          setItems(extractedItems);
          toast.success(`${response.data.total_cortes} peças extraídas com sucesso!`);

          setIsMatching(true);
          try {
            const matchResponse = await quickBudgetService.matchPiecesWithStock(extractedItems);
            if (matchResponse.success) {
              setMatchingResults(matchResponse.data.results);

              const selections = {};
              const prices = {};
              matchResponse.data.results.forEach((result, idx) => {
                if (result.found && result.bestMatch) {
                  selections[idx] = { type: 'matched', variant_id: result.bestMatch.variant_id };
                  prices[idx] = result.suggestedPrice || result.bestMatch.preco_venda || 0;
                } else {
                  selections[idx] = { type: 'pending' };
                  prices[idx] = 0;
                }
              });
              setPieceSelections(selections);
              setManualPrices(prices);

              const found = matchResponse.data.summary.found;
              const notFound = matchResponse.data.summary.notFound;
              toast.success(`${found} peças encontradas no estoque, ${notFound} não encontradas`);
            }
          } catch (matchError) {
            console.error('Erro ao buscar no estoque:', matchError);
            toast.error('Peças extraídas, mas falha ao buscar no estoque');
          } finally {
            setIsMatching(false);
          }

          setCurrentStep(3);
        }
      } else {
        toast.error(response.message || 'Erro ao processar PDF');
      }
    } catch (error) {
      toast.error('Erro ao processar PDF: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setIsUploading(false);
    }
  };

  const handlePriceUpdate = (index, unitPrice) => {
    const updatedItems = [...items];
    updatedItems[index].unit_price = parseFloat(unitPrice) || 0;
    updatedItems[index].total_price = (updatedItems[index].quantity || 1) * (parseFloat(unitPrice) || 0);
    setItems(updatedItems);
  };

  const handleEnvironmentChange = (index, envName) => {
    const updatedItems = [...items];
    updatedItems[index].environment_name = envName;
    setItems(updatedItems);
  };

  const handleItemRemove = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  };

  const handleComplexityChange = (level) => {
    setComplexityLevel(level);
    const complexity = pricingConfig?.complexity?.find(c => c.value === level);
    if (complexity) {
      setComplexityMultiplier(parseFloat(complexity.multiplier));
    }
  };

  const handlePreferenceChange = (type, item) => {
    setPreferences(prev => ({
      ...prev,
      [type]: { key: item.value, weight: parseFloat(item.weight), label: item.label }
    }));
  };

  const convertMutation = useMutation({
    mutationFn: (budgetId) => quickBudgetService.convertToProject(budgetId),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Orçamento convertido em Projeto Oficial com sucesso!');
        setCreatedBudgetModal(null);
        navigate('/projetos');
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Erro ao converter em projeto');
    }
  });

  const handleConvertToProject = (budgetId) => {
    convertMutation.mutate(budgetId);
  };

  const createMutation = useMutation({
    mutationFn: (data) => quickBudgetService.createBudget(data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Orçamento criado com sucesso!');
        setCreatedBudgetModal(response.data);
        handleGeneratePdf(response.data.id);
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Erro ao criar orçamento');
    }
  });

  const generatePdfMutation = useMutation({
    mutationFn: async (id) => {
      const response = await quickBudgetService.generatePDF(id);
      if (response.success) {
        const blob = await quickBudgetService.downloadPDF(id, response.data.fileName);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', response.data.fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
      return response;
    },
    onSuccess: (response) => {
      if (response.success) {
        toast.success('PDF gerado com sucesso!');
      }
    },
    onError: (error) => {
      toast.error('Erro ao gerar PDF');
    }
  });

  const handleGeneratePdf = async (budgetId) => {
    generatePdfMutation.mutate(budgetId);
  };

  const handlePreview = () => {
    if (!selectedClient) {
      toast.error('Selecione um cliente');
      return;
    }
    const hasItems = items.length > 0 || ambientes.some(a => (a.items || []).length > 0);
    if (!hasItems) {
      toast.error('Adicione pelo menos uma peça');
      return;
    }
    setShowPdfPreview(true);
  };

  const handleSubmit = () => {
    if (!selectedClient?.id) {
      toast.error('Selecione um cliente');
      return;
    }

    const hasItems = items.length > 0 || ambientes.some(a => (a.items || []).length > 0);
    if (!hasItems) {
      toast.error('Adicione pelo menos uma peça');
      return;
    }

    const budgetData = {
      client_id: selectedClient.id,
      cortes_pdf_id: cortesPdfId,
      project_name: projectName,
      pricing_type: pricingType,
      ambientes: ambientes.map((amb, idx) => ({
        ...amb,
        item_order: idx
      })),
      items: items.map((item, index) => ({
        ...item,
        item_order: index
      })),
      consumables: consumables.map((c, idx) => ({
        ...c,
        item_order: idx
      })),
      diferenciais,
      inclusoes,
      nao_inclusoes: naoInclusoes,
      discount_percent: discountPercent,
      validity_days: validityDays,
      notes,
      payment_condition: hasDownPayment ? `ENTRADA_${installmentCount}x` : (installmentCount > 1 ? `${installmentCount}x` : 'VISTA'),
      installments: installmentCount,
      down_payment_percent: hasDownPayment ? downPaymentPercent : 0,
      down_payment_method: hasDownPayment ? downPaymentMethod : null,
      installment_method: installmentMethod,
      has_interest: hasInterest,
      consultant_name: consultantName,
      consultant_phone: consultantPhone,
      complexity_multiplier: complexityMultiplier,
      material_weight: totalPreferenceWeight,
      margin_percent: marginPercent,
      base_price: calculatedPricing?.basePrice || baseSubtotal,
      final_price: calculatedPricing?.finalPrice || baseSubtotal,
      pricing_formula: {
        breakdown: [
          { step: 'base', label: 'Base', value: calculatedPricing?.basePrice },
          { step: 'complexity', label: `Complexidade (×${complexityMultiplier})`, value: calculatedPricing?.complexityAdjusted },
          { step: 'material', label: `Preferências (+${totalPreferenceWeight}%)`, value: calculatedPricing?.materialAdjusted },
          { step: 'margin', label: `Margem (+${marginPercent}%)`, value: calculatedPricing?.withMargin },
          { step: 'payment', label: calculatedPricing?.paymentLabel || 'Final', value: calculatedPricing?.finalPrice }
        ]
      }
    };

    createMutation.mutate(budgetData);
  };

  const groupedItems = useMemo(() => {
    const groups = {};
    items.forEach((item, index) => {
      const env = item.environment_name || 'Geral';
      if (!groups[env]) {
        groups[env] = [];
      }
      groups[env].push({ ...item, originalIndex: index });
    });
    return groups;
  }, [items]);

  const envTotal = (envItems) => {
    return envItems.reduce((sum, item) => {
      return sum + ((parseFloat(item.quantity) || 1) * (parseFloat(item.unit_price) || 0));
    }, 0);
  };

  const renderStep1Client = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Selecionar Cliente</h3>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          placeholder="Buscar cliente por nome, telefone ou email..."
          value={clientSearch}
          onChange={(e) => setClientSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(73,148,61)] focus:border-[rgb(73,148,61)]"
        />
      </div>

      {clientSearch && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          {loadingClients ? (
            <div className="p-4 text-center text-gray-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Carregando...
            </div>
          ) : filteredClients.length > 0 ? (
            filteredClients.map((client) => (
              <div
                key={client.id}
                onClick={() => handleClientSelect(client)}
                className="p-4 hover:bg-emerald-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <div className="font-medium text-gray-800">{client.full_name || client.name}</div>
                <div className="text-sm text-gray-500">
                  {client.phone && <span>{client.phone}</span>}
                  {client.email && <span> • {client.email}</span>}
                </div>
              </div>
            ))
          ) : (
            <div className="p-4 text-center text-gray-500">Nenhum cliente encontrado</div>
          )}
        </div>
      )}

      {selectedClient && (
        <div className="mt-6 p-4 bg-emerald-50 border border-[rgb(73,148,61)] rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-emerald-800">{selectedClient.full_name || selectedClient.name}</div>
              <div className="text-sm text-[rgb(73,148,61)]">
                {selectedClient.phone && <span>{selectedClient.phone}</span>}
                {selectedClient.email && <span> • {selectedClient.email}</span>}
              </div>
            </div>
            <Check className="w-6 h-6 text-[rgb(73,148,61)]" />
          </div>
        </div>
      )}
    </div>
  );

  // Step 2: Nome do Projeto
  const renderStep2Project = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Nome do Projeto *
        </label>
        <input
          type="text"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(73,148,61)] focus:border-[rgb(73,148,61)]"
          placeholder="Ex: Apartamento Silva - Cozinha e Closet"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Descrição do Projeto
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(73,148,61)] focus:border-[rgb(73,148,61)]"
          placeholder="Descrição geral do projeto..."
        />
      </div>

      <div className="pt-4">
        <button
          onClick={() => {
            if (!selectedClient?.id) {
              toast.error('Selecione um cliente primeiro');
              return;
            }
            if (!projectName.trim()) {
              toast.error('Digite o nome do projeto');
              return;
            }
            setCurrentStep(3);
          }}
          className="w-full px-6 py-3 bg-[rgb(73,148,61)] text-white rounded-lg hover:bg-[rgb(60,120,50)] font-medium"
        >
          Avançar para Ambientes
        </button>
      </div>
    </div>
  );

  // Step 3: Lista de Ambientes
  const renderStep3Ambientes = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Ambientes do Projeto</h3>
        <button
          onClick={() => {
            const newAmbiente = {
              id: `temp_${Date.now()}`,
              nome: '',
              descricao: '',
              items: [],
              consumables: [],
              status: 'draft'
            };
            setAmbientes([...ambientes, newAmbiente]);
            setEditingAmbienteId(newAmbiente.id);
          }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[rgb(73,148,61)] text-white rounded-lg hover:bg-[rgb(60,120,50)]"
        >
          <Plus className="w-4 h-4" />
          Novo Ambiente
        </button>
      </div>

      {ambientes.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Home className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">Nenhum ambiente adicionado ainda</p>
          <p className="text-sm text-gray-400">Clique em "Novo Ambiente" para começar</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {ambientes.map((ambiente) => (
            <div
              key={ambiente.id}
              className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:border-[rgb(73,148,61)] transition-colors"
            >
              <div>
                <h4 className="font-semibold text-gray-800">{ambiente.nome}</h4>
                {ambiente.descricao && (
                  <p className="text-sm text-gray-500">{ambiente.descricao}</p>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  {ambiente.items?.length || 0} itens
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => openAmbienteEditor(ambiente.id)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  title="Editar ambiente"
                >
                  <Edit2 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    toast.promise(
                      new Promise((resolve) => {
                        setAmbientes(ambientes.filter(a => a.id !== ambiente.id));
                        resolve();
                      }),
                      { success: 'Ambiente removido' }
                    );
                  }}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  title="Remover ambiente"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="pt-4 flex gap-3">
        <button
          onClick={() => setCurrentStep(2)}
          className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
        >
          Voltar
        </button>
        <button
          onClick={() => {
            if (ambientes.length === 0) {
              toast.error('Adicione pelo menos um ambiente');
              return;
            }
            setCurrentStep(4);
          }}
          className="flex-1 px-6 py-3 bg-[rgb(73,148,61)] text-white rounded-lg hover:bg-[rgb(60,120,50)] font-medium"
        >
          Avançar para Consumíveis
        </button>
      </div>
    </div>
  );

  // Editor de Ambiente (quando editingAmbienteId está setado)
  const renderAmbienteEditor = () => {
    const ambiente = ambientes.find(a => a.id === editingAmbienteId);
    if (!ambiente) return null;

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Editar Ambiente
          </h3>
          <button
            onClick={() => {
              setEditingAmbienteId(null);
              setTempAmbienteItems([]);
            }}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Ambiente</label>
          <input
            type="text"
            value={editingAmbiente.nome}
            onChange={(e) => setEditingAmbiente({ ...editingAmbiente, nome: e.target.value })}
            placeholder="Ex: Cozinha, Quarto Principal, Banheiro..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(73,148,61)] focus:border-transparent"
          />
        </div>

        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[rgb(73,148,61)] transition-colors">
          <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-3">Carregar PDF do plano de corte</p>
          <label className="inline-flex items-center gap-2 px-4 py-2 bg-[rgb(73,148,61)] text-white rounded-lg cursor-pointer hover:bg-[rgb(60,120,50)]">
            <Upload className="w-4 h-4" />
            Selecionar PDF
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => handlePdfUpload(e, ambiente.id, ambiente.nome || editingAmbiente.nome)}
              className="hidden"
            />
          </label>
        </div>

        <div className="text-center text-gray-500 py-2">
          <span className="text-sm">- ou -</span>
        </div>

        <button
          onClick={() => {
            const newItem = {
              item_name: '',
              quantity: 1,
              largura_cm: 0,
              comprimento_cm: 0,
              source: 'manual',
              unit_price: 0
            };
            const updated = ambientes.map(a =>
              a.id === editingAmbienteId
                ? { ...a, items: [...(a.items || []), newItem] }
                : a
            );
            setAmbientes(updated);
          }}
          className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Adicionar Item Manual
        </button>

        {ambiente.items?.length > 0 && (
          <div className="mt-4 space-y-3">
            {ambiente.items.map((item, idx) => {
              const matchResult = ambienteMatchResults[ambiente.id]?.[idx];
              const hasMatches = matchResult?.found && matchResult?.matches?.length > 0;
              const showDropdown = hasMatches && matchResult.matches.length > 1;

              return (
                <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-3">
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-4 relative">
                      <input
                        type="text"
                        value={item.item_name || item.name || ''}
                        onChange={(e) => {
                          const updated = ambientes.map(a =>
                            a.id === editingAmbienteId
                              ? {
                                  ...a,
                                  items: a.items.map((it, i) =>
                                    i === idx ? { ...it, item_name: e.target.value } : it
                                  )
                                }
                              : a
                          );
                          setAmbientes(updated);
                        }}
                        placeholder="Nome da peça"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                      {showDropdown && (
                        <select
                          value={item.matched_variant_id || ''}
                          onChange={(e) => {
                            const selectedMatch = matchResult.matches.find(m => m.variant_id === parseInt(e.target.value));
                            if (selectedMatch) {
                              const updated = ambientes.map(a =>
                                a.id === editingAmbienteId
                                  ? {
                                      ...a,
                                      items: a.items.map((it, i) =>
                                        i === idx ? {
                                          ...it,
                                          matched_variant_id: selectedMatch.variant_id,
                                          selection_type: 'matched',
                                          unit_price: selectedMatch.preco_por_m2
                                            ? ((item.largura_cm || 0) * (item.comprimento_cm || 0) / 10000) * selectedMatch.preco_por_m2
                                            : selectedMatch.preco_venda || 0
                                        } : it
                                      )
                                    }
                                  : a
                              );
                              setAmbientes(updated);
                            }
                          }}
                          className="mt-1 w-full px-2 py-1 border border-gray-300 rounded text-xs bg-blue-50"
                        >
                          <option value="">Selecione a variação...</option>
                          {matchResult.matches.map((match, mIdx) => (
                            <option key={match.variant_id} value={match.variant_id}>
                              {match.sku_variacao || `Opção ${mIdx + 1}`} - {match.preco_por_m2 ? `R$ ${match.preco_por_m2}/m²` : `R$ ${match.preco_venda}`}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => {
                        const updated = ambientes.map(a =>
                          a.id === editingAmbienteId
                            ? {
                                ...a,
                                items: a.items.map((it, i) =>
                                  i === idx ? { ...it, quantity: parseInt(e.target.value) || 1 } : it
                                )
                              }
                            : a
                        );
                        setAmbientes(updated);
                      }}
                      placeholder="Qtd"
                      className="col-span-2 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                    />
                    <input
                      type="number"
                      value={item.largura_cm || item.width || ''}
                      onChange={(e) => {
                        const updated = ambientes.map(a =>
                          a.id === editingAmbienteId
                            ? {
                                ...a,
                                items: a.items.map((it, i) =>
                                  i === idx ? { ...it, largura_cm: parseFloat(e.target.value) || 0 } : it
                                )
                              }
                            : a
                        );
                        setAmbientes(updated);
                      }}
                      placeholder="Larg."
                      className="col-span-2 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                    />
                    <input
                      type="number"
                      value={item.comprimento_cm || item.height || ''}
                      onChange={(e) => {
                        const updated = ambientes.map(a =>
                          a.id === editingAmbienteId
                            ? {
                                ...a,
                                items: a.items.map((it, i) =>
                                  i === idx ? { ...it, comprimento_cm: parseFloat(e.target.value) || 0 } : it
                                )
                              }
                            : a
                        );
                        setAmbientes(updated);
                      }}
                      placeholder="Alt."
                      className="col-span-2 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                    />
                    <input
                      type="number"
                      value={item.unit_price}
                      onChange={(e) => {
                        const updated = ambientes.map(a =>
                          a.id === editingAmbienteId
                            ? {
                                ...a,
                                items: a.items.map((it, i) =>
                                  i === idx ? { ...it, unit_price: parseFloat(e.target.value) || 0 } : it
                                )
                              }
                            : a
                        );
                        setAmbientes(updated);
                      }}
                      placeholder="R$"
                      className="col-span-1 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                    />
                    <button
                      onClick={() => {
                        const updated = ambientes.map(a =>
                          a.id === editingAmbienteId
                            ? { ...a, items: a.items.filter((_, i) => i !== idx) }
                            : a
                        );
                        setAmbientes(updated);
                      }}
                      className="col-span-1 text-red-400 hover:text-red-600 p-1 flex items-center justify-center"
                      title="Remover peça"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
              </div>
            );
          })}
          </div>
        )}

        <div className="pt-4 flex gap-3">
          <button
            onClick={() => {
              setEditingAmbienteId(null);
              setTempAmbienteItems([]);
            }}
            className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
          >
            Cancelar
          </button>
          <button
            onClick={() => {
              const updatedAmbientes = ambientes.map(a => {
                if (a.id === editingAmbienteId) {
                  return { ...a, nome: editingAmbiente.nome, descricao: editingAmbiente.descricao };
                }
                return a;
              });
              setAmbientes(updatedAmbientes);
              setEditingAmbienteId(null);
              toast.success('Ambiente salvo!');
            }}
            className="flex-1 px-6 py-3 bg-[rgb(73,148,61)] text-white rounded-lg hover:bg-[rgb(60,120,50)] font-medium"
          >
            Salvar Ambiente
          </button>
        </div>
      </div>
    );
  };

  // Step 4: Consumíveis Gerais (não específicos de ambiente)
  const renderStep4Consumables = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Consumíveis e Acessórios</h3>
        <button
          onClick={() => {
            setConsumables([
              ...consumables,
              {
                id: Date.now(),
                name: '',
                quantity: 1,
                unit_price: 0,
                type: 'accessory'
              }
            ]);
          }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[rgb(73,148,61)] text-white rounded-lg hover:bg-[rgb(60,120,50)]"
        >
          <Plus className="w-4 h-4" />
          Adicionar
        </button>
      </div>

      <p className="text-sm text-gray-500">
        Adicione consumíveis e acessórios gerais do projeto (ex: cola, parafusos, puxadores, etc.)
      </p>

      <div className="relative consumable-search-dropdown">
        <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
          <Search className="w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={consumableSearch}
            onChange={(e) => {
              setConsumableSearch(e.target.value);
              searchConsumables(e.target.value);
            }}
            placeholder="Buscar produto no estoque..."
            className="flex-1 bg-transparent border-none outline-none text-sm"
          />
          {isSearchingConsumables && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
        </div>
        {showConsumableDropdown && consumableSearchResults.length > 0 && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {consumableSearchResults.map((product, idx) => (
              <button
                key={product.variant_id || idx}
                onClick={() => handleSelectConsumable(product)}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
              >
                <div className="text-sm font-medium text-gray-800">{product.produto_nome}</div>
                <div className="text-xs text-gray-500">
                  {product.categoria_nome && `${product.categoria_nome}`}
                  {product.medida_text && ` | ${product.medida_text}`}
                  {product.cor && ` | ${product.cor}`}
                </div>
                <div className="text-xs text-[rgb(73,148,61)] font-medium">
                  R$ {parseFloat(product.preco_venda || 0).toFixed(2)}
                  {product.estoque_disponivel > 0 && ` | Estoque: ${product.estoque_disponivel}`}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {consumables.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Package className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">Nenhum consumível adicionado</p>
        </div>
      ) : (
        <div className="space-y-3">
          {consumables.map((item, idx) => (
            <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="grid grid-cols-12 gap-2 items-center">
                <input
                  type="text"
                  value={item.name}
                  onChange={(e) => {
                    const updated = [...consumables];
                    updated[idx].name = e.target.value;
                    setConsumables(updated);
                  }}
                  placeholder="Nome do item"
                  className="col-span-5 px-2 py-1 border border-gray-300 rounded text-sm"
                />
                <input
                  type="number"
                  value={item.quantity}
                  onChange={(e) => {
                    const updated = [...consumables];
                    updated[idx].quantity = parseInt(e.target.value) || 1;
                    setConsumables(updated);
                  }}
                  placeholder="Qtd"
                  className="col-span-2 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                />
                <input
                  type="number"
                  value={item.unit_price}
                  onChange={(e) => {
                    const updated = [...consumables];
                    updated[idx].unit_price = parseFloat(e.target.value) || 0;
                    setConsumables(updated);
                  }}
                  placeholder="R$ unit"
                  className="col-span-3 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                />
                <button
                  onClick={() => setConsumables(consumables.filter((_, i) => i !== idx))}
                  className="col-span-2 text-red-500 hover:text-red-700 p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="pt-4 flex gap-3">
        <button
          onClick={() => setCurrentStep(3)}
          className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
        >
          Voltar
        </button>
        <button
          onClick={() => setCurrentStep(5)}
          className="flex-1 px-6 py-3 bg-[rgb(73,148,61)] text-white rounded-lg hover:bg-[rgb(60,120,50)] font-medium"
        >
          Avançar para Precificação
        </button>
      </div>
    </div>
  );

  const renderStep5Pricing = () => {
    const hasAnyItems = ambientes.some(a => (a.items || []).length > 0);
    const allItemsTotal = getAllItemsTotal();
    const allConsumablesTotal = getAllConsumablesTotal();

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Precificação do Projeto</h3>
          {isLoadingConfig && <Loader2 className="w-5 h-5 animate-spin text-[rgb(73,148,61)]" />}
        </div>

        {!hasAnyItems ? (
          <div className="text-center py-8 text-gray-500">
            Adicione peças nos ambientes para definir a precificação
          </div>
        ) : (
          <>
            <div className="bg-[rgb(73,148,61)] bg-opacity-10 border border-[rgb(73,148,61)] rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-5 h-5 text-[rgb(73,148,61)]" />
                <span className="font-medium text-[rgb(73,148,61)]">Base de Cálculo</span>
              </div>
              <div className="text-2xl font-bold text-gray-800">
                {quickBudgetService.formatCurrency(allItemsTotal + allConsumablesTotal)}
              </div>
              <div className="text-sm text-gray-500 mb-2">
                Itens + Consumíveis
              </div>
              <div className="bg-white/50 rounded p-2 text-xs space-y-1">
                {ambientes.map((amb) => (
                  <div key={amb.id}>
                    <div className="font-medium text-[rgb(73,148,61)] border-b border-gray-200 pb-1 mb-1">
                      {amb.nome || 'Ambiente'}
                    </div>
                    {(amb.items || []).map((item, idx) => (
                      <div key={idx} className="flex justify-between">
                        <span>{item.item_name || 'Peça'}: {item.source === 'pdf' ? `${(item.quantity || 1)}× R$ ${parseFloat(item.unit_price).toFixed(2)}` : `R$ ${parseFloat(item.unit_price).toFixed(2)}`}</span>
                        <span className="font-medium">{quickBudgetService.formatCurrency(getItemTotal(item))}</span>
                      </div>
                    ))}
                    {(amb.consumables || []).length > 0 && (
                      <div className="flex justify-between border-t border-gray-200 pt-1 mt-1">
                        <span className="text-gray-500">Consumíveis de {amb.nome}</span>
                        <span className="font-medium">{quickBudgetService.formatCurrency((amb.consumables || []).reduce((s, c) => s + ((parseFloat(c.quantity) || 1) * (parseFloat(c.unit_price) || 0)), 0))}</span>
                      </div>
                    )}
                  </div>
                ))}
                {allConsumablesTotal > 0 && (
                  <div className="flex justify-between border-t border-[rgb(73,148,61)] pt-1 mt-1">
                    <span>Consumíveis Gerais</span>
                    <span className="font-medium">{quickBudgetService.formatCurrency(totalConsumables)}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-5 h-5 text-[rgb(73,148,61)]" />
                  <span className="font-medium text-gray-800">Complexidade do Projeto</span>
                </div>
                <div className="space-y-2">
                  {pricingConfig?.complexity?.map((c) => (
                    <button
                      key={c.value}
                      onClick={() => handleComplexityChange(c.value)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        complexityLevel === c.value
                          ? 'border-[rgb(73,148,61)] bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-sm">{c.label}</div>
                        <div className="text-xs text-gray-500">{c.description}</div>
                      </div>
                      <div className={`font-bold ${complexityLevel === c.value ? 'text-[rgb(73,148,61)]' : 'text-gray-600'}`}>
                          ×{parseFloat(c.multiplier).toFixed(2)}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Settings className="w-5 h-5 text-[rgb(73,148,61)]" />
                <span className="font-medium text-gray-800">Tipo de Material</span>
              </div>
              <div className="space-y-2">
                {pricingConfig?.materials
                  ?.filter(m => m.type === 'material_type')
                  .map((m) => (
                    <button
                      key={m.value}
                      onClick={() => handlePreferenceChange('material_type', m)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        preferences.material_type.key === m.value
                          ? 'border-[rgb(73,148,61)] bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-sm">{m.label}</div>
                          <div className="text-xs text-gray-500">{m.description}</div>
                        </div>
                        <div className={`font-bold ${preferences.material_type.key === m.value ? 'text-[rgb(73,148,61)]' : 'text-gray-600'}`}>
                          {parseFloat(m.weight) >= 0 ? '+' : ''}{parseFloat(m.weight).toFixed(0)}%
                        </div>
                      </div>
                    </button>
                  ))}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Settings className="w-5 h-5 text-[rgb(73,148,61)]" />
                <span className="font-medium text-gray-800">Acabamento de Porta</span>
              </div>
              <div className="space-y-2">
                {pricingConfig?.materials
                  ?.filter(m => m.type === 'door_finish')
                  .map((m) => (
                    <button
                      key={m.value}
                      onClick={() => handlePreferenceChange('door_finish', m)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        preferences.door_finish.key === m.value
                          ? 'border-[rgb(73,148,61)] bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-sm">{m.label}</div>
                          <div className="text-xs text-gray-500">{m.description}</div>
                        </div>
                        <div className={`font-bold ${preferences.door_finish.key === m.value ? 'text-[rgb(73,148,61)]' : 'text-gray-600'}`}>
                          {parseFloat(m.weight) >= 0 ? '+' : ''}{parseFloat(m.weight).toFixed(0)}%
                        </div>
                      </div>
                    </button>
                  ))}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Settings className="w-5 h-5 text-[rgb(73,148,61)]" />
                <span className="font-medium text-gray-800">Tipo de Ferragem</span>
              </div>
              <div className="space-y-2">
                {pricingConfig?.materials
                  ?.filter(m => m.type === 'hardware_type')
                  .map((m) => (
                    <button
                      key={m.value}
                      onClick={() => handlePreferenceChange('hardware_type', m)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        preferences.hardware_type.key === m.value
                          ? 'border-[rgb(73,148,61)] bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-sm">{m.label}</div>
                          <div className="text-xs text-gray-500">{m.description}</div>
                        </div>
                        <div className={`font-bold ${preferences.hardware_type.key === m.value ? 'text-[rgb(73,148,61)]' : 'text-gray-600'}`}>
                          {parseFloat(m.weight) >= 0 ? '+' : ''}{parseFloat(m.weight).toFixed(0)}%
                        </div>
                      </div>
                    </button>
                  ))}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Settings className="w-5 h-5 text-[rgb(73,148,61)]" />
                <span className="font-medium text-gray-800">Vidro</span>
              </div>
              <div className="space-y-2">
                {pricingConfig?.materials
                  ?.filter(m => m.type === 'has_glass')
                  .map((m) => (
                    <button
                      key={m.value}
                      onClick={() => handlePreferenceChange('has_glass', m)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        preferences.has_glass.key === m.value
                          ? 'border-[rgb(73,148,61)] bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-sm">{m.label}</div>
                          <div className="text-xs text-gray-500">{m.description}</div>
                        </div>
                        <div className={`font-bold ${preferences.has_glass.key === m.value ? 'text-[rgb(73,148,61)]' : 'text-gray-600'}`}>
                          {parseFloat(m.weight) >= 0 ? '+' : ''}{parseFloat(m.weight).toFixed(0)}%
                        </div>
                      </div>
                    </button>
                  ))}
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Percent className="w-5 h-5 text-[rgb(73,148,61)]" />
              <span className="font-medium text-gray-800">Margem de Lucro</span>
            </div>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="5"
                max="500"
                value={marginPercent}
                onChange={(e) => setMarginPercent(parseInt(e.target.value))}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[rgb(73,148,61)]"
              />
              <div className="w-20 text-center">
                <span className="text-2xl font-bold text-[rgb(73,148,61)]">{marginPercent}%</span>
              </div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span>5% (Mín)</span>
              <span>100% (Padrão)</span>
              <span>500% (Máx)</span>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="w-5 h-5 text-[rgb(73,148,61)]" />
              <span className="font-medium text-gray-800">Condições de Pagamento</span>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-800">Entrada</div>
                  <div className="text-xs text-gray-500">Pagamento inicial</div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setHasDownPayment(false)}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                      !hasDownPayment
                        ? 'bg-[rgb(73,148,61)] text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    Não
                  </button>
                  <button
                    onClick={() => setHasDownPayment(true)}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                      hasDownPayment
                        ? 'bg-[rgb(73,148,61)] text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    Sim
                  </button>
                </div>
              </div>

              {hasDownPayment && (
                <>
                  <div className="p-3 border border-[rgb(73,148,61)] rounded-lg bg-emerald-50">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Porcentagem da entrada: {downPaymentPercent}%
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="50"
                      step="5"
                      value={downPaymentPercent}
                      onChange={(e) => setDownPaymentPercent(parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[rgb(73,148,61)]"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>10%</span>
                      <span>50%</span>
                    </div>
                    <div className="mt-2 text-right text-sm font-medium text-[rgb(73,148,61)]">
                      Valor: {quickBudgetService.formatCurrency(pricingCalculation.downPaymentAmount)}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium text-gray-800">Desconto</div>
                      <div className="text-xs text-gray-500">Aplicar desconto no total</div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setDownPaymentHasDiscount(false)}
                        className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                          !downPaymentHasDiscount
                            ? 'bg-[rgb(73,148,61)] text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        Não
                      </button>
                      <button
                        onClick={() => setDownPaymentHasDiscount(true)}
                        className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                          downPaymentHasDiscount
                            ? 'bg-[rgb(73,148,61)] text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        Sim
                      </button>
                    </div>
                  </div>

                  {downPaymentHasDiscount && (
                    <div className="p-3 border border-[rgb(73,148,61)] rounded-lg bg-emerald-50">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Porcentagem do desconto: {downPaymentDiscountPercent}%
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="15"
                        step="1"
                        value={downPaymentDiscountPercent}
                        onChange={(e) => setDownPaymentDiscountPercent(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[rgb(73,148,61)]"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>1%</span>
                        <span>15%</span>
                      </div>
                      <div className="mt-2 text-right text-sm font-medium text-[rgb(73,148,61)]">
                        Economia: {quickBudgetService.formatCurrency(pricingCalculation.discountAmount)}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Como paga?
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { value: 'DINHEIRO', label: '💵 Dinheiro' },
                        { value: 'PIX', label: '📱 Pix' },
                        { value: 'DEBIT', label: '💳 Débito' },
                        { value: 'CREDIT', label: '🃏 Crédito' }
                      ].map(method => (
                        <button
                          key={method.value}
                          onClick={() => setDownPaymentMethod(method.value)}
                          className={`p-2 rounded-lg border text-xs font-medium transition-all ${
                            downPaymentMethod === method.value
                              ? 'border-[rgb(73,148,61)] bg-emerald-50 text-[rgb(73,148,61)]'
                              : 'border-gray-200 hover:border-gray-300 text-gray-600'
                          }`}
                        >
                          <div>{method.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="p-3 border border-gray-200 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número de parcelas: {installmentCount}x
                </label>
                <input
                  type="range"
                  min="2"
                  max="24"
                  value={installmentCount}
                  onChange={(e) => setInstallmentCount(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[rgb(73,148,61)]"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>2x</span>
                  <span>24x</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Como paga as parcelas?
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { value: 'CREDIT', label: '💳 Crédito' },
                    { value: 'BOLETO', label: '📄 Boleto' },
                    { value: 'PIX', label: '📱 Pix' },
                    { value: 'DINHEIRO', label: '💵 Dinheiro' }
                  ].map(method => (
                    <button
                      key={method.value}
                      onClick={() => setInstallmentMethod(method.value)}
                      className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                        installmentMethod === method.value
                          ? 'border-[rgb(73,148,61)] bg-emerald-50 text-[rgb(73,148,61)]'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      {method.label}
                    </button>
                  ))}
                </div>
              </div>

              {installmentCount > 1 && installmentMethod === 'CREDIT' && (
                <div className="p-3 border border-[rgb(73,148,61)] rounded-lg bg-emerald-50">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quem paga a taxa do cartão?
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setRatePaidBy('empresa')}
                      className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                        ratePaidBy === 'empresa'
                          ? 'border-[rgb(73,148,61)] bg-emerald-100 text-[rgb(73,148,61)]'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      Empresa (sem juros)
                    </button>
                    <button
                      onClick={() => setRatePaidBy('cliente')}
                      className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                        ratePaidBy === 'cliente'
                          ? 'border-[rgb(73,148,61)] bg-emerald-100 text-[rgb(73,148,61)]'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      Cliente (com taxa)
                    </button>
                  </div>
                  {ratePaidBy === 'cliente' && pricingCalculation.appliedCardRate > 0 && (
                    <div className="mt-2 text-xs text-[rgb(73,148,61)] font-medium">
                      Taxa de {(pricingCalculation.appliedCardRate * 100).toFixed(2)}% será incluída no valor
                    </div>
                  )}
                </div>
              )}

              {installmentCount > 1 && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-800">Juros</div>
                    <div className="text-xs text-gray-500">Aplicar correção (1.5% a.m.)</div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setHasInterest(false)}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                        !hasInterest
                          ? 'bg-[rgb(73,148,61)] text-white'
                          : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                      }`}
                    >
                      Não
                    </button>
                    <button
                      onClick={() => setHasInterest(true)}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                        hasInterest
                          ? 'bg-[rgb(73,148,61)] text-white'
                          : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                      }`}
                    >
                      Sim
                    </button>
                  </div>
                </div>
              )}

              {pricingCalculation.remaining > 0 && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Entrada:</span>
                    <span className="font-medium">{quickBudgetService.formatCurrency(pricingCalculation.downPaymentAmount)}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Financiado:</span>
                    <span className="font-medium">{quickBudgetService.formatCurrency(pricingCalculation.remaining)}</span>
                  </div>
                  {pricingCalculation.interestAmount > 0 && (
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Juros:</span>
                      <span className="font-medium text-red-500">+{quickBudgetService.formatCurrency(pricingCalculation.interestAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-semibold pt-2 border-t border-blue-200">
                    <span className="text-gray-800">Valor das parcelas:</span>
                    <span className="text-[rgb(73,148,61)]">
                      {installmentCount}x de {quickBudgetService.formatCurrency(pricingCalculation.parcelValue)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-900 text-white rounded-xl p-6">
            <h4 className="text-lg font-semibold mb-4 text-center">Fórmula de Cálculo</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Base:</span>
                <span className="font-medium">{quickBudgetService.formatCurrency(pricingCalculation.basePrice)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Complexidade (×{complexityMultiplier.toFixed(2)}):</span>
                <span className="font-medium">{quickBudgetService.formatCurrency(pricingCalculation.complexityAdjusted)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Preferências (+{totalPreferenceWeight}%):</span>
                <span className="font-medium">{quickBudgetService.formatCurrency(pricingCalculation.materialAdjusted)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Margem (+{marginPercent}%):</span>
                <span className="font-medium">{quickBudgetService.formatCurrency(pricingCalculation.withMargin)}</span>
              </div>
              {pricingCalculation.paymentLabel && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Pagamento:</span>
                  <span className="font-medium text-emerald-400">
                    {pricingCalculation.paymentLabel}
                  </span>
                </div>
              )}
              <div className="border-t border-gray-700 pt-2 mt-2 flex justify-between items-center">
                <span className="text-lg font-bold">VALOR FINAL:</span>
                <span className="text-2xl font-bold text-[rgb(135,183,74)]">
                  {quickBudgetService.formatCurrency(pricingCalculation.finalPrice)}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
  };

  const renderStep6Review = () => {
    const allItemsCount = ambientes.reduce((sum, a) => sum + (a.items || []).length, 0);
    const allConsumablesCount = ambientes.reduce((sum, a) => sum + (a.consumables || []).length, 0) + consumables.length;

    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Revisar Orçamento</h3>

        <div className="bg-emerald-50 border border-[rgb(73,148,61)] rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-[rgb(73,148,61)]">Cliente</div>
              <div className="font-semibold">{selectedClient?.full_name || selectedClient?.name || '-'}</div>
            </div>
            <div>
              <div className="text-sm text-[rgb(73,148,61)]">Projeto</div>
              <div className="font-semibold">{projectName || '-'}</div>
            </div>
            <div>
              <div className="text-sm text-[rgb(73,148,61)]">Ambientes</div>
              <div className="font-semibold">{ambientes.length}</div>
            </div>
            <div>
              <div className="text-sm text-[rgb(73,148,61)]">Peças</div>
              <div className="font-semibold">{allItemsCount} item(s)</div>
            </div>
            <div>
              <div className="text-sm text-[rgb(73,148,61)]">Base</div>
              <div className="font-semibold">{quickBudgetService.formatCurrency(getAllItemsTotal() + getAllConsumablesTotal())}</div>
            </div>
            <div>
              <div className="text-sm text-[rgb(73,148,61)]">Consumíveis</div>
              <div className="font-semibold">{allConsumablesCount} item(s)</div>
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-medium mb-3">Itens por Ambiente:</h4>
          <div className="space-y-3">
            {ambientes.map((amb) => {
              const ambItemsTotal = (amb.items || []).reduce((sum, item) => sum + getItemTotal(item), 0);
              const ambConsTotal = (amb.consumables || []).reduce((sum, c) => sum + ((parseFloat(c.quantity) || 1) * (parseFloat(c.unit_price) || 0)), 0);
              return (
                <div key={amb.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-medium text-lg">{amb.nome || 'Ambiente sem nome'}</div>
                    <div className="font-semibold text-[rgb(73,148,61)]">
                      {quickBudgetService.formatCurrency(ambItemsTotal + ambConsTotal)}
                    </div>
                  </div>
                  {(amb.items || []).length > 0 && (
                    <div className="space-y-2 mb-3">
                      <div className="text-xs text-gray-500 font-medium border-b pb-1">Peças:</div>
                      {(amb.items || []).map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <div>
                            <div className="text-gray-800">{item.item_name || 'Peça sem nome'}</div>
                            <div className="text-gray-500 text-xs">
                              {(item.quantity || 1)}x • {item.source === 'pdf' ? quickBudgetService.formatCurrency(parseFloat(item.unit_price) || 0) : quickBudgetService.formatCurrency((parseFloat(item.unit_price) || 0) / (parseFloat(item.quantity) || 1))} cada
                            </div>
                          </div>
                          <div className="font-medium">
                            {quickBudgetService.formatCurrency(getItemTotal(item))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {(amb.consumables || []).length > 0 && (
                    <div className="space-y-2 border-t pt-2">
                      <div className="text-xs text-gray-500 font-medium border-b pb-1">Consumíveis:</div>
                      {(amb.consumables || []).map((c, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <div className="text-gray-600">{c.name || 'Consumível'}</div>
                          <div className="font-medium text-gray-600">
                            {quickBudgetService.formatCurrency((parseFloat(c.quantity) || 1) * (parseFloat(c.unit_price) || 0))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {consumables.length > 0 && (
          <div>
            <h4 className="font-medium mb-3">Consumíveis Gerais:</h4>
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2">
              {consumables.map((c, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <div className="text-gray-600">{c.name || 'Consumível'}</div>
                  <div className="font-medium text-gray-600">
                    {quickBudgetService.formatCurrency((parseFloat(c.quantity) || 1) * (parseFloat(c.unit_price) || 0))}
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between text-sm font-semibold border-t border-gray-200 pt-2 mt-2">
                <div className="text-[rgb(73,148,61)]">Total Consumíveis Gerais</div>
                <div className="text-[rgb(73,148,61)]">
                  {quickBudgetService.formatCurrency(totalConsumables)}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Desconto (%)
            </label>
            <input
              type="number"
              value={discountPercent || ''}
              onChange={(e) => setDiscountPercent(parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              min="0"
              max="100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Validade (dias)
            </label>
            <input
              type="number"
              value={validityDays || ''}
              onChange={(e) => setValidityDays(parseInt(e.target.value) || 30)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Observações
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            rows="3"
            placeholder="Observações adicionais..."
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nome do Consultor
          </label>
          <input
            type="text"
            value={consultantName}
            onChange={(e) => setConsultantName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Telefone do Consultor
          </label>
          <input
            type="text"
            value={consultantPhone}
            onChange={(e) => setConsultantPhone(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
      </div>
    </div>
  );
  };

  const renderStep7Summary = () => {
    const allItemsCount = ambientes.reduce((sum, a) => sum + (a.items || []).length, 0);

    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Resumo do Orçamento</h3>

        <div className="bg-emerald-50 border border-[rgb(73,148,61)] rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-[rgb(73,148,61)]">Cliente</div>
              <div className="font-semibold">{selectedClient?.full_name || selectedClient?.name || '-'}</div>
            </div>
            <div>
              <div className="text-sm text-[rgb(73,148,61)]">Projeto</div>
              <div className="font-semibold">{projectName || '-'}</div>
            </div>
            <div>
              <div className="text-sm text-[rgb(73,148,61)]">Ambientes</div>
              <div className="font-semibold">{ambientes.length}</div>
            </div>
            <div>
              <div className="text-sm text-[rgb(73,148,61)]">Validade</div>
              <div className="font-semibold">{validityDays} dias</div>
            </div>
            <div>
              <div className="text-sm text-[rgb(73,148,61)]">Parcelas</div>
              <div className="font-semibold">
                {hasDownPayment
                  ? `Entrada ${downPaymentPercent}% + ${installmentCount}x`
                  : installmentCount > 1
                    ? `${installmentCount}x${hasInterest ? ' c/ juros' : ' s/ juros'}`
                    : 'À vista'}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium mb-3 text-center">Fórmula de Precificação</h4>
          <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Base (preço peças):</span>
              <span className="font-medium">{quickBudgetService.formatCurrency(pricingCalculation?.basePrice || getAllItemsTotal())}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Complexidade (×{complexityMultiplier}):</span>
              <span className="font-medium text-[rgb(73,148,61)]">
                {quickBudgetService.formatCurrency(pricingCalculation?.complexityAdjusted || 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Preferências (+{totalPreferenceWeight}%):</span>
              <span className="font-medium text-[rgb(73,148,61)]">
                {quickBudgetService.formatCurrency(pricingCalculation?.materialAdjusted || 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Margem (+{marginPercent}%):</span>
              <span className="font-medium text-[rgb(73,148,61)]">
                {quickBudgetService.formatCurrency(pricingCalculation?.withMargin || 0)}
              </span>
            </div>
            {pricingCalculation?.paymentLabel && (
              <div className="flex justify-between">
                <span className="text-gray-600">{pricingCalculation?.paymentLabel}</span>
                <span className="font-medium text-[rgb(73,148,61)]">
                  {quickBudgetService.formatCurrency(pricingCalculation?.finalPrice || 0)}
                </span>
              </div>
            )}
            <div className="border-t-2 border-gray-300 pt-2 mt-2 flex justify-between items-center">
              <span className="text-lg font-bold">VALOR FINAL:</span>
              <span className="text-2xl font-bold text-[rgb(73,148,61)]">
                {quickBudgetService.formatCurrency(pricingCalculation?.finalPrice || 0)}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-gray-900 text-white rounded-lg p-6">
          <div className="text-center">
            <div className="text-gray-300 mb-2">VALOR TOTAL</div>
            <div className="text-4xl font-bold mb-2">
              {quickBudgetService.formatCurrency(pricingCalculation?.finalPrice || 0)}
            </div>
          {pricingCalculation?.paymentLabel && (
            <div className="text-sm text-emerald-400 mb-2">
              ({pricingCalculation?.paymentLabel})
            </div>
          )}
          <div className="text-gray-300">
            {hasDownPayment
              ? `Entrada ${downPaymentPercent}% + ${installmentCount}x de ${quickBudgetService.formatCurrency(pricingCalculation?.parcelValue || 0)}`
              : installmentCount > 1
                ? `em até ${installmentCount}x de ${quickBudgetService.formatCurrency(pricingCalculation?.parcelValue || 0)}`
                : ''}
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-medium mb-3">Diferenciais:</h4>
        <div className="flex flex-wrap gap-2">
          {diferenciais.map((diff, idx) => (
            <span key={idx} className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm">
              ✓ {diff}
            </span>
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-medium mb-3">O que está incluso:</h4>
        <div className="flex flex-wrap gap-2">
          {inclusoes.map((inc, idx) => (
            <span key={idx} className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm">
              ✓ {inc}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
  };

  const renderCurrentStep = () => {
    // Se estiver editando um ambiente, mostra o editor de ambiente
    if (editingAmbienteId) {
      return renderAmbienteEditor();
    }
    switch (currentStep) {
      case 1: return renderStep1Client();
      case 2: return renderStep2Project();
      case 3: return renderStep3Ambientes();
      case 4: return renderStep4Consumables();
      case 5: return renderStep5Pricing();
      case 6: return renderStep6Review();
      case 7: return renderStep7Summary();
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-[rgb(73,148,61)] to-[rgb(73,148,61)] p-6">
            <h1 className="text-2xl font-bold text-white">Orçamento Rápido</h1>
            <p className="text-emerald-100">Crie orçamentos em minutos com precificação completa</p>
          </div>

          <div className="p-6">
            <div className="flex items-center justify-between mb-8">
              {STEPS.map((step, index) => {
                const Icon = step.icon;
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;

                return (
                  <React.Fragment key={step.id}>
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isCompleted
                            ? 'bg-[rgb(73,148,61)] text-white'
                            : isActive
                            ? 'bg-emerald-100 text-[rgb(73,148,61)] border-2 border-[rgb(73,148,61)]'
                            : 'bg-gray-200 text-gray-400'
                        }`}
                      >
                        {isCompleted ? (
                          <Check className="w-5 h-5" />
                        ) : (
                          <Icon className="w-5 h-5" />
                        )}
                      </div>
                      <div className={`text-xs mt-1 ${isActive || isCompleted ? 'text-[rgb(73,148,61)] font-medium' : 'text-gray-400'}`}>
                        {step.name}
                      </div>
                    </div>

                    {index < STEPS.length - 1 && (
                      <div className={`flex-1 h-1 mx-2 rounded ${currentStep > step.id ? 'bg-[rgb(73,148,61)]' : 'bg-gray-200'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            <div className="min-h-[400px]">
              {renderCurrentStep()}
            </div>

            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                disabled={currentStep === 1}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg ${
                  currentStep === 1
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ArrowLeft className="w-4 h-4" />
                Anterior
              </button>

              {currentStep < 6 ? (
                <button
                  onClick={() => {
                    if (currentStep === 2 && hasNoCuttingPlan) {
                      if (manualBudgetMode === 'TOTAL' && parseFloat(manualTotalValue) > 0) {
                        setItems([{
                          source: 'manual',
                          item_name: 'Projeto Personalizado',
                          quantity: 1,
                          width_cm: 0,
                          height_cm: 0,
                          material: 'Personalizado',
                          color: '-',
                          unit_price: parseFloat(manualTotalValue),
                          environment: 'Geral'
                        }]);
                      } else if (manualBudgetMode === 'ITENS' && manualItems.length > 0) {
                        const convertedItems = manualItems.map((item, idx) => ({
                          source: 'manual',
                          item_name: item.name || `Peça ${idx + 1}`,
                          quantity: item.quantity || 1,
                          width_cm: item.width || 0,
                          height_cm: item.height || 0,
                          material: item.material || 'Personalizado',
                          color: item.color || '-',
                          finish: item.finish || '',
                          thickness_mm: item.thickness || '',
                          unit_price: item.price || 0,
                          measure_text: item.measure_text || '',
                          variant_id: item.variant_id || null,
                          pricePerSqm: item.pricePerSqm || 0,
                          environment: 'Geral'
                        }));
                        setItems(convertedItems);

                        const newMatchingResults = [...matchingResults];
                        const newPieceSelections = { ...pieceSelections };
                        const newManualPrices = { ...manualPrices };

                        manualItems.forEach((item, idx) => {
                          if (item.variant_id) {
                            newMatchingResults[idx] = {
                              found: true,
                              matches: [{
                                variant_id: item.variant_id,
                                product_name: item.name,
                                thickness_mm: item.thickness,
                                preco_por_m2: item.pricePerSqm,
                                medida_text: item.measure_text
                              }]
                            };
                            newPieceSelections[idx] = {
                              type: 'matched',
                              variant_id: item.variant_id
                            };
                            newManualPrices[idx] = item.pricePerSqm;
                          }
                        });

                        setMatchingResults(newMatchingResults);
                        setPieceSelections(newPieceSelections);
                        setManualPrices(newManualPrices);
                      }
                    }
                    setCurrentStep(Math.min(6, currentStep + 1));
                  }}
                  disabled={
                    (currentStep === 1 && !selectedClient) ||
                    (currentStep === 2 && !cortesPdfId && !(hasNoCuttingPlan && (parseFloat(manualTotalValue) > 0 || manualItems.length > 0))) ||
                    (currentStep === 3 && items.length === 0)
                  }
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg ${
                    (currentStep === 1 && !selectedClient) ||
                    (currentStep === 2 && !cortesPdfId && !(hasNoCuttingPlan && (parseFloat(manualTotalValue) > 0 || manualItems.length > 0))) ||
                    (currentStep === 3 && items.length === 0)
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-[rgb(73,148,61)] text-white hover:bg-[rgb(60,120,50)]'
                  }`}
                >
                  Próximo
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <button
                    onClick={handlePreview}
                    className="flex items-center gap-2 px-6 py-3 bg-white border border-[rgb(73,148,61)] text-[rgb(73,148,61)] rounded-lg hover:bg-emerald-50"
                  >
                    <Eye className="w-4 h-4" />
                    Ver Prévia
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={createMutation.isPending}
                    className="flex items-center gap-2 px-6 py-3 bg-[rgb(73,148,61)] text-white rounded-lg hover:bg-[rgb(60,120,50)] disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    {createMutation.isPending ? 'Salvando...' : 'Criar Orçamento'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showPdfPreview && (
        <PdfPreviewModal
          budget={mapQuickBudgetToTemplate({
            selectedClient,
            projectName,
            ambientes,
            items,
            consumables,
            diferenciais,
            inclusoes,
            naoInclusoes,
            notes,
            validityDays,
            pricingCalculation,
            consultantName,
            consultantPhone,
            complexityMultiplier,
            totalPreferenceWeight,
            marginPercent
          })}
          onClose={() => setShowPdfPreview(false)}
        />
      )}

      {createdBudgetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-5 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-[rgb(73,148,61)]">
                  <CheckCircle className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Orçamento Criado com Sucesso!</h3>
                  <p className="text-xs text-gray-500">Nº {createdBudgetModal.budget_number}</p>
                </div>
              </div>
              <button
                onClick={() => setCreatedBudgetModal(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm border border-gray-200">
              <div className="flex justify-between">
                <span className="text-gray-600">Cliente:</span>
                <span className="font-semibold text-gray-800">{createdBudgetModal.client_name || selectedClient?.full_name || selectedClient?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Projeto:</span>
                <span className="font-semibold text-gray-800">{createdBudgetModal.project_name || projectName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Valor Total:</span>
                <span className="font-bold text-[rgb(73,148,61)] text-base">
                  {quickBudgetService.formatCurrency(createdBudgetModal.final_price || createdBudgetModal.total_amount || 0)}
                </span>
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <button
                onClick={() => handleConvertToProject(createdBudgetModal.id)}
                disabled={convertMutation.isPending}
                className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-50"
              >
                <FolderPlus className="w-5 h-5" />
                {convertMutation.isPending ? 'Convertendo em Projeto...' : 'Converter em Projeto Oficial Agora'}
              </button>

              <button
                onClick={() => handleGeneratePdf(createdBudgetModal.id)}
                disabled={generatePdfMutation.isPending}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors text-sm"
              >
                <Download className="w-4 h-4" />
                {generatePdfMutation.isPending ? 'Baixando PDF...' : 'Baixar PDF Novamente'}
              </button>

              <button
                onClick={() => {
                  setCreatedBudgetModal(null);
                  navigate('/orcamentos');
                }}
                className="w-full text-center text-xs text-gray-400 hover:text-gray-600 py-1"
              >
                Fechar e ver lista de orçamentos
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickBudget;
